import { useState, type FormEvent } from 'react';
import { color } from '../styles/theme';
import { useRequestReset } from '../api/account';
import { Button, inputStyle } from './ui';
import { IconSpinner } from './icons';
import { AuthShell } from './AuthShell';

/**
 * Request a reset link.
 *
 * The confirmation never says whether the address matched an account: telling
 * a stranger which emails are admins here is an enumeration oracle, and the
 * API answers identically either way for the same reason.
 */
export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const requestReset = useRequestReset();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await requestReset.mutateAsync(email);
    } catch {
      // Deliberately ignored — see above. A failure here must look like success.
    }
    setSent(true);
  }

  if (sent) {
    return (
      <AuthShell title="ENACT Lab" sub="Check your email">
        <p style={{ fontSize: 13.5, color: color.ink, margin: 0, lineHeight: 1.6 }}>
          If <strong>{email}</strong> belongs to an admin account, a link to set a new password is
          on its way. The link can be used once and expires.
        </p>
        <Button variant="ghost" onClick={onBack} style={{ justifyContent: 'center' }}>
          Back to sign in
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="ENACT Lab"
      sub="Reset your password"
      onSubmit={submit}
      footnote="Only accounts that already exist can be reset. Ask an owner for an invite otherwise."
    >
      <label style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: color.ink }}>Email</span>
        <input
          type="email"
          value={email}
          autoComplete="username"
          required
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle}
        />
      </label>

      <Button
        type="submit"
        disabled={requestReset.isPending || email.trim().length === 0}
        style={{ justifyContent: 'center', marginTop: 4 }}
      >
        {requestReset.isPending && <IconSpinner size={15} />}
        {requestReset.isPending ? 'Sending…' : 'Send reset link'}
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="hov-blue-text"
        style={{
          background: 'transparent',
          border: 'none',
          color: color.blue,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          padding: 4,
        }}
      >
        Back to sign in
      </button>
    </AuthShell>
  );
}
